import React from 'react';
import { InvoiceLineItem } from '@/services/api/invoice';

interface Props {
  lineItems: InvoiceLineItem[];
  isLoading: boolean;
  oemName: string;
  period: string;
}

const formatRupiah = (n: number): string =>
  'Rp ' + n.toLocaleString('id-ID');

const FLAG_LABELS: Record<string, string> = {
  missing_contract_start: 'Missing contract start date',
  zero_daily_rate: 'Zero daily rate',
};

const InvoiceTable: React.FC<Props> = ({
  lineItems,
  isLoading,
  oemName,
  period,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Plate', 'Hub', 'Contract Start', 'Active Days', 'Rate/Day', 'Subtotal'].map(
                (col) => (
                  <th
                    key={col}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    {col}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="border-b border-gray-100">
                {Array.from({ length: 6 }).map((__, j) => (
                  <td key={j} className="px-4 py-3">
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (lineItems.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-12 text-center mb-6">
        <i className="ri-file-list-3-line text-[48px] text-gray-300 block mb-3" />
        <p className="text-gray-500 text-sm">
          No active vehicles for <strong>{oemName || 'the selected OEM'}</strong> in{' '}
          <strong>{period || 'the selected period'}</strong>. Check that contract start
          dates are recorded correctly.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Plate
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Hub
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Contract Start
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Active Days
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Rate/Day
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Subtotal
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lineItems.map((li) => (
              <tr
                key={li.vehicleID}
                className={li.isFlagged ? 'bg-amber-50' : 'hover:bg-gray-50'}
              >
                <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                  {li.isFlagged && (
                    <i
                      className="ri-alert-line text-amber-500 mr-1"
                      title={FLAG_LABELS[li.flagReason ?? ''] ?? li.flagReason ?? ''}
                    />
                  )}
                  {li.platNomor}
                  {li.flagReason && (
                    <span className="block text-xs text-amber-600 font-normal mt-0.5">
                      {FLAG_LABELS[li.flagReason] ?? li.flagReason}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {li.hubName ?? <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {li.contractStartDate ?? (
                    <span className="text-red-500 text-xs font-medium">Missing</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {li.activeDays}
                </td>
                <td className="px-4 py-3 text-right text-gray-600">
                  {formatRupiah(li.dailyRate)}
                </td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">
                  {li.isFlagged ? (
                    <span className="text-amber-600">—</span>
                  ) : (
                    formatRupiah(li.subtotal)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceTable;
